import React, { useMemo, useState, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { useQuery } from 'react-query';
import {
	Box,
	Chip,
	TextField,
	MenuItem,
	CircularProgress,
	Alert,
	Tooltip,
	IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import { talentPoolService } from '../../../services';

/**
 * TalentPool-hakemusten taulukkonomponetti
 * Näyttää kaikki TalentPool-hakemukset taulukossa suodattamis- ja sivutusmahdollisuuksilla
 */
const TalentPoolApplicationsTable = () => {
	const navigate = useNavigate();
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 10,
	});

	// Hae hakemukset React Queryn avulla
	const { data, isLoading, error, refetch, isFetching } = useQuery(
		['talentPoolApplications', pagination.pageIndex, pagination.pageSize],
		() =>
			talentPoolService.getTalentPoolApplications({
				page: pagination.pageIndex + 1,
				limit: pagination.pageSize,
			}),
		{
			keepPreviousData: true,
			staleTime: 5 * 60 * 1000, // 5 minuuttia
		},
	);

	// Tilavalinnat
	const statusOptions = [
		{ value: 1, label: 'Arvioitu', color: 'primary' },
		{ value: 2, label: 'Hyväksytty', color: 'success' },
		{ value: 3, label: 'Hylätty', color: 'error' },
	];

	// Muodosta tilan näyttöön käytettävä Chip-komponentti
	const getStatusChip = useCallback((status) => {
		const statusOption = statusOptions.find(
			(option) => option.value === status,
		);
		if (!statusOption) return null;

		return (
			<Chip
				label={statusOption.label}
				color={statusOption.color}
				size="small"
			/>
		);
	}, []);

	// Määrittele taulukon sarakkeet
	const columns = useMemo(
		() => [
			{
				accessorKey: 'name',
				header: 'Nimi',
				size: 180,
			},
			{
				accessorKey: 'email',
				header: 'Sähköposti',
				size: 200,
			},
			{
				accessorKey: 'responses.form_response.definition.title',
				header: 'Hakemuksen otsikko',
				size: 200,
			},
			{
				accessorKey: 'university',
				header: 'Yliopisto',
				size: 180,
			},
			{
				accessorKey: 'major',
				header: 'Pääaine',
				size: 180,
			},
			{
				accessorKey: 'category',
				header: 'Kategoria',
				size: 180,
				Cell: ({ cell }) => (
					<Box>
						{cell.getValue()?.map((cat, index) => (
							<Chip
								key={index}
								label={cat}
								size="small"
								sx={{ mr: 0.5, mb: 0.5 }}
							/>
						))}
					</Box>
				),
			},
			{
				accessorKey: 'score',
				header: 'Pisteet',
				size: 100,
				Cell: ({ cell }) => {
					const score = cell.getValue();
					let color = 'success.main';
					if (score < 12) color = 'error.main';
					else if (score < 18) color = 'warning.main';

					return (
						<Box
							component="span"
							sx={{
								backgroundColor: color,
								borderRadius: '0.25rem',
								color: '#fff',
								p: '0.25rem 0.5rem',
								fontWeight: 'bold',
							}}
						>
							{score}
						</Box>
					);
				},
			},
			{
				accessorKey: 'status',
				header: 'Tila',
				size: 120,
				Cell: ({ cell }) => getStatusChip(cell.getValue()),
				Filter: ({ column }) => (
					<TextField
						select
						label="Tila"
						value={column.getFilterValue() ?? ''}
						onChange={(e) => column.setFilterValue(e.target.value)}
						variant="outlined"
						size="small"
						fullWidth
					>
						<MenuItem value="">Kaikki</MenuItem>
						{statusOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				),
			},
			{
				accessorKey: 'createdAt',
				header: 'Lähetetty',
				size: 120,
				Cell: ({ cell }) =>
					new Date(cell.getValue()).toLocaleDateString('fi-FI'),
			},
		],
		[getStatusChip],
	);

	// Viimeisin päivitysaika
	const lastUpdated = useMemo(() => {
		return new Date().toLocaleTimeString('fi-FI');
	}, [data]);

	// Tyhjien rivien näyttämiseen käytettävä komponentti
	const EmptyRowsComponent = () => {
		if (isLoading) return <CircularProgress />;
		if (error)
			return (
				<Alert severity="error">Virhe hakemusten lataamisessa</Alert>
			);
		return <Alert severity="info">Ei hakemuksia saatavilla</Alert>;
	};

	// Käsittele data oikeassa muodossa
	const tableData = useMemo(() => {
		if (!data || !data.results) return [];
		return data.results;
	}, [data]);

	// Kokonaismäärä sivutukseen
	const totalRowCount = useMemo(() => {
		if (!data) return 0;
		return data.totalResults || 0;
	}, [data]);

	// Käsittele hakemuksen klikkaus ja navigointi
	const handleRowClick = useCallback(
		(row) => {
			const applicationId = row.original.id;

			// Jos ID on undefined, yritä käyttää _id-kenttää
			if (!applicationId && row.original._id) {
				navigate(`/app/talentpool-applications/${row.original._id}`);
				return;
			}

			// Jos ID on saatavilla, käytä sitä
			if (applicationId) {
				navigate(`/app/talentpool-applications/${applicationId}`);
				return;
			}

			console.error('Hakemuksen ID puuttuu:', row.original);
		},
		[navigate],
	);

	return (
		<Box sx={{ mt: 2 }}>
			<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
				<Box sx={{ flexGrow: 1 }} />
				<Tooltip title="Päivitä">
					<IconButton onClick={() => refetch()} disabled={isFetching}>
						<RefreshIcon />
					</IconButton>
				</Tooltip>
			</Box>
			<MaterialReactTable
				columns={columns}
				data={tableData}
				initialState={{ showColumnFilters: true }}
				manualPagination
				rowCount={totalRowCount}
				onPaginationChange={setPagination}
				state={{
					pagination,
					isLoading,
					showProgressBars: isFetching,
				}}
				enableGlobalFilter={false}
				muiToolbarAlertBannerProps={
					error
						? {
								color: 'error',
								children: 'Virhe hakemusten lataamisessa',
							}
						: undefined
				}
				muiTableBodyRowProps={({ row }) => ({
					onClick: () => handleRowClick(row),
					sx: { cursor: 'pointer' },
				})}
				renderEmptyRowsFallback={EmptyRowsComponent}
				renderTopToolbarCustomActions={() => (
					<Box
						sx={{
							display: 'flex',
							gap: 2,
							alignItems: 'center',
							p: 1,
						}}
					>
						{!isLoading && !error && (
							<Box
								component="span"
								sx={{
									fontSize: '0.75rem',
									color: 'text.secondary',
								}}
							>
								Päivitetty: {lastUpdated}
							</Box>
						)}
					</Box>
				)}
				// Sivutus
				paginationDisplayMode="pages"
				positionPagination="bottom"
				// Lokalisoidut tekstit
				localization={{
					actions: 'Toiminnot',
					and: 'ja',
					cancel: 'Peruuta',
					clearFilter: 'Poista suodatin',
					clearSearch: 'Tyhjennä haku',
					hideAll: 'Piilota kaikki',
					showAll: 'Näytä kaikki',
					rowsPerPage: 'Rivejä per sivu',
					of: '/',
					pagination: {
						firstTooltip: 'Ensimmäinen sivu',
						lastTooltip: 'Viimeinen sivu',
						nextTooltip: 'Seuraava sivu',
						previousTooltip: 'Edellinen sivu',
					},
				}}
			/>
		</Box>
	);
};

export default TalentPoolApplicationsTable;
